import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import { api } from "../lib/api";

// Inline formatting: **bold** and [text](url)
function renderInline(text, keyBase) {
  const nodes = [];
  let remaining = text;
  let idx = 0;
  const pattern = /(\*\*([^*]+)\*\*)|(\[([^\]]+)\]\(([^)]+)\))/;
  while (remaining.length) {
    const m = remaining.match(pattern);
    if (!m) { nodes.push(remaining); break; }
    if (m.index > 0) nodes.push(remaining.slice(0, m.index));
    if (m[1]) {
      nodes.push(<strong key={`${keyBase}-b-${idx++}`} className="text-white font-semibold">{m[2]}</strong>);
    } else if (m[3]) {
      nodes.push(
        <a key={`${keyBase}-a-${idx++}`} href={m[5]} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">{m[4]}</a>
      );
    }
    remaining = remaining.slice(m.index + m[0].length);
  }
  return nodes;
}

// Block parser: ## / ### headings, --- dividers, * / - bullet lists, paragraphs.
function renderContent(text) {
  const lines = text.split("\n");
  const blocks = [];
  let list = [];
  const flushList = (i) => {
    if (list.length) {
      blocks.push(
        <ul key={`ul-${i}`} className="list-disc pl-5 space-y-2 mb-5 text-zinc-400">
          {list.map((item, k) => <li key={k}>{renderInline(item, `li-${i}-${k}`)}</li>)}
        </ul>
      );
      list = [];
    }
  };

  lines.forEach((raw, i) => {
    const t = raw.trim();
    if (!t) { flushList(i); return; }
    if (t === "---") { flushList(i); blocks.push(<hr key={`hr-${i}`} className="border-white/10 my-8" />); return; }
    if (t.startsWith("# ")) { return; } // H1 skipped (page title already shown)
    if (t.startsWith("### ")) {
      flushList(i);
      blocks.push(<h3 key={`h3-${i}`} className="font-heading font-bold text-base text-white mt-6 mb-2">{renderInline(t.slice(4), `h3-${i}`)}</h3>);
      return;
    }
    if (t.startsWith("## ")) {
      flushList(i);
      blocks.push(<h2 key={`h2-${i}`} className="font-heading font-bold text-xl text-white mt-9 mb-3">{renderInline(t.slice(3), `h2-${i}`)}</h2>);
      return;
    }
    if (t.startsWith("* ") || t.startsWith("- ")) { list.push(t.slice(2)); return; }
    flushList(i);
    blocks.push(<p key={`p-${i}`} className="text-zinc-400 leading-relaxed mb-4">{renderInline(t, `p-${i}`)}</p>);
  });
  flushList(lines.length);
  return blocks;
}

export default function Legal() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setDoc(null);
    setErr("");
    api.get(`/content/legal/${slug}`).then((r) => setDoc(r.data)).catch(() => setErr("Document not found."));
  }, [slug]);

  return (
    <div data-testid="legal-page" className="pt-32 pb-24 min-h-screen relative">
      <Seo title={doc ? `${doc.title} — ASCENDXP` : "ASCENDXP"} description={`ASCENDXP ${doc?.title || "legal"} document.`} />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 relative">
        {err ? (
          <p className="text-red-400">{err}</p>
        ) : !doc ? (
          <div className="flex items-center gap-3 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        ) : (
          <>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">{doc.title}</h1>
            <p className="text-xs text-zinc-600 mt-3">Version {doc.version} · Updated {new Date(doc.updated_at).toLocaleDateString()}</p>
            <div className="mt-10 glass rounded-2xl p-8 sm:p-10">{renderContent(doc.content)}</div>
          </>
        )}
      </div>
    </div>
  );
}
